import { Icon, notification, Spin } from "antd";
import React from "react";

interface DownloadEntry {
  id: string;
  name: string;
  status: "downloading" | "complete" | "error";
  errorMessage?: string;
}

const NOTIFICATION_KEY = "download-manager";
const activeDownloads = new Map<string, DownloadEntry>();
let downloadCounter = 0;

function renderNotificationContent() {
  const entries = Array.from(activeDownloads.values());
  if (entries.length === 0) return null;

  const allDone = entries.every(e => e.status !== "downloading");
  const hasErrors = entries.some(e => e.status === "error");
  const activeCount = entries.filter(e => e.status === "downloading").length;
  const totalCount = entries.length;

  const heading = allDone
    ? hasErrors
      ? "Completed with errors"
      : "All downloads complete"
    : `Downloading ${activeCount} of ${totalCount} report${totalCount > 1 ? "s" : ""}`;

  return (
    <div style={{ margin: "-4px -8px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#e8e8e8", marginBottom: 2 }}>
        {heading}
      </div>
      {!allDone && (
        <div style={{ fontSize: 10, color: "#8c8c8c", marginBottom: 4 }}>
          You can continue working. Don't close or refresh this tab.
        </div>
      )}
      <div style={{ maxHeight: 140, overflowY: "auto" }}>
        {entries.map(entry => (
          <div
            key={entry.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 0"
            }}
          >
            {entry.status === "downloading" && <Spin size="small" />}
            {entry.status === "complete" && (
              <Icon type="check-circle" theme="filled" style={{ color: "#52c41a", fontSize: 14 }} />
            )}
            {entry.status === "error" && (
              <Icon type="close-circle" theme="filled" style={{ color: "#f5222d", fontSize: 14 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                title={entry.name}
                style={{
                  fontSize: 12,
                  color: "#d9d9d9",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: "16px"
                }}
              >
                {entry.name}
              </div>
              <div style={{ fontSize: 10, color: "#8c8c8c", lineHeight: "14px" }}>
                {entry.status === "downloading" && "Generating..."}
                {entry.status === "complete" && "Downloaded"}
                {entry.status === "error" && (entry.errorMessage || "Failed")}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function updateNotification() {
  const entries = Array.from(activeDownloads.values());
  if (entries.length === 0) {
    notification.close(NOTIFICATION_KEY);
    return;
  }

  const allDone = entries.every(e => e.status !== "downloading");

  notification.open({
    key: NOTIFICATION_KEY,
    message: null as any,
    description: renderNotificationContent(),
    icon: null as any,
    placement: "bottomLeft" as any,
    duration: allDone ? 5 : 0,
    className: "download-notification",
    style: {
      width: 200,
      padding: "10px 14px",
      background: "#262626",
      borderRadius: 6,
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.35)"
    },
    onClose: () => {
      for (const [id, entry] of activeDownloads) {
        if (entry.status !== "downloading") {
          activeDownloads.delete(id);
        }
      }
    }
  });
}

/**
 * Wraps an async download function with a persistent notification.
 * Supports multiple concurrent downloads — each tracked independently.
 */
export async function downloadWithNotification(
  reportName: string,
  downloadFn: () => Promise<void>
): Promise<void> {
  const id = `download-${++downloadCounter}`;
  activeDownloads.set(id, { id, name: reportName, status: "downloading" });
  updateNotification();

  try {
    await downloadFn();
    activeDownloads.set(id, { id, name: reportName, status: "complete" });
  } catch (err: any) {
    activeDownloads.set(id, {
      id,
      name: reportName,
      status: "error",
      errorMessage: err?.message || "Download failed"
    });
    throw err;
  } finally {
    updateNotification();
    setTimeout(() => {
      const entry = activeDownloads.get(id);
      if (entry && entry.status !== "downloading") {
        activeDownloads.delete(id);
        updateNotification();
      }
    }, 10000);
  }
}
